import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { InlineWidget } from 'react-calendly';
import { IPlan } from '../../types/plans';
import { mutations, queries } from '../../graphql/graphql';
import { useMutation, useQuery } from '@apollo/client';
import { Loader } from '../../components/UI/Loader';
import { Helmet } from 'react-helmet';
import { Affix, Col, Grid, Row } from 'antd';
import { PlanViewImage } from '../../components/Plans/View/PlanImageView';
import { getTitle, getAreaTitle } from '../../utils/utils';
import { PlanInfo } from '../../components/Plans/View/PlanInfoView';
import { PlanMapView } from '../../components/Plans/View/PlanMapView';
import { PlanProcessView } from '../../components/Plans/View/PlanProcessView';
import { PhotoGallery } from '../../components/Plans/View/PhotoGallery';
import Summary from '../../components/Plans/View/Summary';
import { blockTitle } from '../../components/Plans/View/styles';
import Recommendation from '../../components/Plans/View/Recommendation';
import OnboardingModal from '../../components/UI/OnboardingModal';
import { planViewOnboarding } from '../Map/constants/onboarding';

const { useBreakpoint } = Grid;

interface PlanViewProps {
  id?: string;
  mapId?: string;
  onNext?: () => void;
  hideSimilarHomes?: boolean;
}

export const PlanView: React.FC<PlanViewProps> = props => {
  let { id, mapId } = useParams();
  const [isScrolled, setIsScrolled] = useState(false);
  const screens = useBreakpoint();
  const pricesRef = useRef<HTMLDivElement>(null);
  const calendlyRef = useRef<HTMLDivElement>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  if (!id) id = props.id;
  if (!mapId) mapId = props.mapId;
  const { data: mapSessionData, loading: mapSessionLoading } = useQuery(
    queries.mapSession.GET_MAP_SESSION,
    {
      fetchPolicy: 'network-only',
    }
  );
  const [MAP_SESSION_UPDATE] = useMutation(mutations.mapSession.UPDATE_MAP_SESSION);

  useEffect(() => {
    if (mapSessionData?.mapSession && !mapSessionLoading) {
      const hasCompletedOnboardingPlanView =
        mapSessionData.mapSession.hasCompletedOnboardingPlanView;
      if (!hasCompletedOnboardingPlanView) {
        setShowOnboarding(true);
      }
    }
  }, [mapSessionData, mapSessionLoading]);

  const { data, loading, called, refetch } = useQuery<{ planView: IPlan }>(
    queries.plans.PLAN_VIEW,
    {
      variables: { id },
      fetchPolicy: 'network-only',
    }
  );

  const {
    data: summaryData,
    loading: summaryLoading,
    refetch: refetchSummary,
  } = useQuery(queries.plans.GET_SUMMARY, {
    variables: { id },
  });

  useEffect(() => {
    refetch();
    refetchSummary();
  }, [refetch, refetchSummary]);

  const { data: plansCountData } = useQuery(queries.plans.GET_PLANS_COUNT);
  console.log(id);
  if (!loading && called && !data) return <div>Plan not found</div>;
  if (loading || summaryLoading) return <Loader styles={{ marginTop: '40vh' }} />;

  if (!data?.planView) return <div>Oops, something went wrong</div>;

  const { meta, isModular, isManufactured } = data?.planView;

  const title = getTitle(meta);
  const areaTitle = getAreaTitle(meta);
  const metaTitle = (title + ' ' + areaTitle).toLowerCase();

  const handleOnboardingComplete = () => {
    MAP_SESSION_UPDATE({
      variables: {
        input: {
          hasCompletedOnboardingPlanView: true,
        },
      },
    });
    setShowOnboarding(false);
  };

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaTitle} />
      </Helmet>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Row
            justify="space-around"
            align="stretch"
            style={{
              backgroundColor: '#F5F5F5',
              width: '100%',
              textAlign: 'left',
            }}>
            <Col
              span={24}
              lg={16}
              style={{ height: !screens.lg ? 'auto' : 'calc(100dvh - 130px)' }}>
              <PlanViewImage image={data.planView.image} photos={data.planView.photos || []} />
            </Col>
            <Col
              span={24}
              lg={8}
              style={{
                flexDirection: 'column',
                display: 'flex',
                maxHeight: 'calc(100dvh - 130px)',
              }}>
              {screens.lg ? (
                <Affix onChange={value => setIsScrolled(!!value)}>
                  <div
                    style={{
                      height: isScrolled ? '100dvh' : 'calc(100dvh - 130px)',
                      overflowY: 'auto',
                      backgroundColor: '#F5F5F5',
                      transition: 'height 0.4s ease-in-out',
                    }}>
                    <PlanInfo
                      plan={data.planView}
                      summary={summaryData?.getSummary.data}
                      title={title}
                      areaTitle={areaTitle}
                      calendlyRef={calendlyRef}
                      pricesRef={pricesRef}
                      ctaLabel={props.onNext ? 'Next' : undefined}
                      onNext={props.onNext}
                    />
                  </div>
                </Affix>
              ) : (
                <PlanInfo
                  plan={data.planView}
                  summary={summaryData?.getSummary.data}
                  title={title}
                  areaTitle={areaTitle}
                  calendlyRef={calendlyRef}
                  pricesRef={pricesRef}
                  ctaLabel={props.onNext ? 'Next' : undefined}
                  onNext={props.onNext}
                />
              )}
            </Col>
          </Row>
        </Col>
        <Col span={24} lg={16} style={{ padding: 20 }}>
          <PlanProcessView />
        </Col>
        <Col span={24} lg={16} style={{ padding: 20 }}>
          <PlanMapView mapId={mapId} plan={data.planView} />
        </Col>
        <Col span={24} lg={16} style={{ padding: 20 }}>
          <PhotoGallery photos={data.planView.photos || []} />
        </Col>
        {!isModular && !isManufactured && (
          <Col span={24} lg={16} style={{ padding: 20 }} ref={pricesRef}>
            <Summary data={summaryData?.getSummary.data} url={data.planView.url} />
          </Col>
        )}
        <Col span={24} lg={16} style={{ padding: 20 }}>
          <span
            style={{
              ...blockTitle,
              maxWidth: '800px',
              margin: 'auto',
              fontSize: '20px',
              paddingBottom: 0,
            }}>
            Our library carries{' '}
            {plansCountData?.plansCount.total ? plansCountData?.plansCount.total : 512} more plans
            that might work. Our &quot;Sorting Hat&quot; is intelligent but sometimes a discussion
            between two humans will result in a better solution. Let us talk.
          </span>
        </Col>
        <Col span={24} lg={16} style={{ padding: 20 }}>
          <div ref={calendlyRef} style={{ marginTop: '1em' }}>
            <InlineWidget
              styles={{ height: '1260px' }}
              url="https://calendly.com/homeworkinprogress/30min"
            />
          </div>
        </Col>
        {!props.hideSimilarHomes && (
          <Col span={24} lg={16} style={{ padding: 20 }}>
            <Recommendation plan={data.planView} />
          </Col>
        )}
      </Row>

      <OnboardingModal
        open={showOnboarding}
        onClose={handleOnboardingComplete}
        image={planViewOnboarding.image}
        title={planViewOnboarding.title}
        badge={planViewOnboarding.badge}
        buttonText={planViewOnboarding.buttonText}
        content={planViewOnboarding.content}
        onButtonClick={handleOnboardingComplete}
      />
    </>
  );
};
